import { Injectable, inject } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';

import { LoggingService } from '../core/logging.service';
import { EnvironmentService } from '../core/environment.service';

// Query-parameter names whose value must be masked in logs. Case-insensitive
// substring match, so e.g. "customer_email" and "cardNumber" are both caught.
const PII_PARAM_NAME = /(email|phone|password|token|secret|auth|credential|card|cvc|cvv|ssn)/i;

export function maskSensitiveUrl(url: string): string {
  const questionMarkIndex = url.indexOf('?');
  if (questionMarkIndex === -1) {
    return url;
  }
  const path = url.slice(0, questionMarkIndex);
  const query = url.slice(questionMarkIndex + 1);
  const maskedQuery = query
    .split('&')
    .map(pair => {
      const equalsIndex = pair.indexOf('=');
      if (equalsIndex === -1) {
        return pair;
      }
      const key = pair.slice(0, equalsIndex);
      const rawValue = pair.slice(equalsIndex + 1);
      let decodedValue = rawValue;
      try {
        decodedValue = decodeURIComponent(rawValue);
      } catch {
        // keep raw value if decoding fails
      }
      if (PII_PARAM_NAME.test(key) || decodedValue.includes('@')) {
        return `${key}=***`;
      }
      return pair;
    })
    .join('&');
  return `${path}?${maskedQuery}`;
}

@Injectable()
export class LoggingInterceptor implements HttpInterceptor {
  private loggingService = inject(LoggingService);
  private environmentService = inject(EnvironmentService);


  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const started = Date.now();
    let ok: string;
    let error: Error | null = null;

    return next.handle(request)
      .pipe(
        tap(
          event => ok = event instanceof HttpResponse ? 'succeeded' : '',
          err => {
            ok = 'failed';
            error = err;
          }
        ),
        finalize(() => {
          const elapsed = Date.now() - started;
          const safeUrl = maskSensitiveUrl(request.urlWithParams);
          const msg = `${request.method} "${safeUrl}"
             ${ok} in ${elapsed} ms.`;

          // Request tracing is a non-prod dev aid; prod requests should not
          // leak URLs (which may still embed order tokens in path segments)
          // into the browser console where end-users can see them.
          if (!this.environmentService.resolve().production) {
            this.loggingService.log(msg);
          }

          if (error) {
            this.loggingService.error(error);
          }
        })
      );
  }
}
